import api from '@/lib/api'
import router from '@/router'
import { Ability } from '@casl/ability'

const ability = new Ability()
const tokenKey = `${window.location.hostname}:strongline:auth:token`
const keyKey = `${window.location.hostname}:strongline:auth:key`
const roleKey = `${window.location.hostname}:strongline:auth:role`
const params = new URLSearchParams(window.location.search)

if (params.get('token')) {
  localStorage[tokenKey] = JSON.stringify({
    token: params.get('token'),
    expiresAt: Number(params.get('expiresAt') || new Date('3000'))
  })
}
if (params.get('key')) {
  sessionStorage[keyKey] = params.get('key')
}
if (params.get('role')) {
  sessionStorage[roleKey] = params.get('role')
}

function loadRefreshToken () {
  try {
    return localStorage[tokenKey] && JSON.parse(localStorage[tokenKey])
  } catch (error) {}
}

// initial state
const state = {
  user: null,
  roles: null,
  loading: true,
  valid: true,
  accessToken: null,
  refreshToken: loadRefreshToken(),
  key: sessionStorage[keyKey],
  roleOverride: sessionStorage[roleKey]
}

// getters
const getters = {
  user: state => state.user,
  redirectError: state => params.get('authError') === '1',
  redirectFail: state => params.get('authFail') === '1',
  accessToken: state => state.accessToken,
  refreshToken: state => state.refreshToken,
  key: state => state.key,
  loading: state => state.loading,
  valid: state => state.valid,
  ability: () => ability,
  can: () => ability.can.bind(ability),
  roles: state => state.roles || [],
  roleOverride: state => state.roleOverride
}

// actions
const actions = {
  login: async ({ commit, state, getters }, after = null) => {
    if (getters.key || getters.refreshToken) {
      try {
        commit('SET_LOADING', true)
        const params = {
          ...(getters.roleOverride && { role: getters.roleOverride })
        }
        const { data: user } = await api.get('/users/me', { params })
        getters.ability.update(user.abilities || [])
        commit('SET_USER', user)
        const { data: roles } = await api.get('/users/roles')
        commit('SET_ROLES', roles)
      } catch (error) {
        commit('SET_USER', null)
        commit('SET_KEY', null)
        commit('SET_ROLE_OVERRIDE', null)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    }
    if (after && router.currentRoute.name !== after) {
      router.push({ name: after })
    } else if (router.currentRoute.path !== '/' &&
        !['device', 'deviceV1'].includes(router.currentRoute.name)) {
      router.push('/')
    }
  },
  logout: async ({ commit, getters }, redirectParams) => {
    commit('SET_LOADING', true)
    try {
      const loggedIn = getters.user
      const newSlug = ((redirectParams || {}).query || {}).slug
      getters.ability.update([])
      commit('SET_USER', null)
      commit('SET_REFRESH_TOKEN', null)
      commit('SET_ACCESS_TOKEN', null)
      commit('SET_KEY', null)
      commit('SET_ROLE_OVERRIDE', null)
      commit('SET_SLUG', newSlug, { root: true })
      commit('SET_VALID', true)
      window.sessionStorage.clear()
      if (redirectParams) {
        router.push(redirectParams)
      } else if (loggedIn) {
        window.location.href = '/login'
      }
    } catch (error) {
      console.log(error)
    } finally {
      commit('SET_LOADING', false)
    }
  },
  valid: ({ commit }, valid) => {
    commit('SET_VALID', valid)
  }
}

// mutations
const mutations = {
  SET_USER (state, user) {
    state.user = user
  },
  SET_ROLES (state, roles) {
    state.roles = roles
  },
  SET_LOADING (state, loading) {
    state.loading = loading
  },
  SET_ACCESS_TOKEN (state, token) {
    state.accessToken = token
  },
  SET_REFRESH_TOKEN (state, token) {
    if (token) {
      localStorage[tokenKey] = JSON.stringify(token)
    } else {
      delete localStorage[tokenKey]
    }
    state.refreshToken = token
  },
  SET_KEY (state, key) {
    if (key) {
      sessionStorage[keyKey] = key
    } else {
      delete sessionStorage[keyKey]
    }
    state.key = key
  },
  SET_ROLE_OVERRIDE (state, role) {
    if (role) {
      sessionStorage[roleKey] = role
    } else {
      delete sessionStorage[roleKey]
    }
    state.roleOverride = role
  },
  SET_VALID (state, valid) {
    state.valid = !!valid
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
